import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { localeToDir, localeToHtmlLang } from '@/utils/seo';

/**
 * Keeps `<html lang>` and `<html dir>` in step with the locale across
 * client-side navigations.
 *
 * `_document.tsx` sets both attributes, but it only ever runs on the server.
 * The language switcher changes locale with `router.push(…, { locale })`, which
 * swaps the page's translations without re-rendering the `<html>` element, so
 * the attributes keep whatever the first server render put on them: switching
 * to Arabic left the page LTR until a reload, and switching back out of it left
 * the Portuguese copy mirrored. Both only looked right after F5, which is the
 * one thing that re-runs `_document`.
 *
 * The resolution mirrors `_document` exactly — same `pageProps.htmlLang`
 * override, same `en` fallback — so a client transition can never land on a
 * lang/dir pair the server would not have produced for that same URL.
 *
 * Writing to the DOM from an effect rather than rendering the attributes is not
 * a shortcut: `<html>` lives above the React root in the pages router, so it is
 * not ours to render outside `_document`.
 */
export function useDocumentLanguage(htmlLangOverride?: string): void {
  const router = useRouter();
  const htmlLang = htmlLangOverride ?? localeToHtmlLang(router.locale ?? 'en');

  useEffect(() => {
    const root = document.documentElement;

    const applyToDocument = () => {
      root.lang = htmlLang;
      root.dir = localeToDir(htmlLang);
    };

    applyToDocument();

    // Both mechanisms below exist to undo the same line in Next's client router,
    // which assigns the RAW routing locale to `<html lang>` itself:
    //
    //   if (nextState.locale) document.documentElement.lang = nextState.locale;
    //     — next/dist/shared/lib/router/router.js
    //
    // It assumes the routing locale is already a language tag. Ours are not, for
    // half the catalogue: the router stamps `sa`, `cn`, `jp`, `kr`, `pt` where
    // the document must say `ar`, `zh-Hans`, `ja`, `ko`, `pt-BR`. `dir` is never
    // written by the router, which is why only `lang` regressed.
    //
    // 1. routeChangeComplete — ordinary navigations. The router writes the raw
    //    locale immediately before emitting it, so the listener is the first
    //    point at which the correct tag survives. Not limited to locale
    //    switches: every internal link is a transition.
    const events = router?.events;
    events?.on('routeChangeComplete', applyToDocument);

    // 2. MutationObserver — the write that emits nothing. After hydration Next
    //    issues an internal `replace(..., { _h: 1 })` for any getStaticProps page
    //    reached with a query string or matching middleware's `config.matcher`
    //    (ours matches every 2-segment path, and 96 of our pages are SSG — so
    //    this is the normal case, not an edge one). That pass runs the same
    //    `lang` write while skipping BOTH routeChangeStart and
    //    routeChangeComplete, so no listener can see it. Watching the attribute
    //    is the only thing that can. Scoped to one attribute on one element, and
    //    the equality guard makes it self-terminating: re-applying sets `lang`
    //    to the value being compared against, so the resulting mutation is a
    //    no-op and the callback stops.
    //
    // The one write the observer must NOT undo is Chrome's full-page
    // translation: it rewrites `lang` to the language it translated INTO and
    // marks the document with `translated-ltr`/`translated-rtl`. Reverting that
    // would put the observer and the translator in a write-revert loop for as
    // long as the tab stays open, and the translated copy's own language is the
    // honest value while it is on screen — `dir` included, since the visitor may
    // well have translated into Arabic. So the observer stands down instead.
    const isTranslated = () => root.classList.contains('translated-ltr') || root.classList.contains('translated-rtl');

    const observer = new MutationObserver(() => {
      if (isTranslated()) return;
      if (root.lang !== htmlLang) applyToDocument();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['lang'] });

    return () => {
      events?.off('routeChangeComplete', applyToDocument);
      observer.disconnect();
    };
  }, [htmlLang, router?.events]);
}
